import React, { useState } from 'react';
import { Trash2, TrendingUp, Car, ChevronDown, ChevronUp } from 'lucide-react';
import { SimulatorCartItem, TimePeriod, BalneotherapyType } from '../../types';
import { RevenueEvolution } from '../../../../utils/simulatorCalculations';
import { convertDailyToDisplayQuantity } from '../../../../utils/simulatorCalculations';
import { generateNgapCodeLabel } from '../../../../utils/urlUtils';
import { getDisplacementValue } from '../../../../utils/pricingUtils';
import PeriodConversionTooltip from '../PeriodConversionTooltip';
import BalneotherapySelector from '../BalneotherapySelector';

interface SimulatorCartRowProps {
  item: SimulatorCartItem;
  evolution?: RevenueEvolution;
  selectedPeriod: TimePeriod;
  onUpdateQuantity: (itemId: string, newQuantity: number) => void;
  onUpdateHomeVisitPercentage: (itemId: string, percentage: number) => void;
  onUpdateBalneotherapy: (itemId: string, balneotherapy: BalneotherapyType) => void;
  onRemoveFromCart: (itemId: string) => void;
  darkMode: boolean;
}

const SimulatorCartRow: React.FC<SimulatorCartRowProps> = ({
  item,
  evolution,
  selectedPeriod,
  onUpdateQuantity,
  onUpdateHomeVisitPercentage,
  onUpdateBalneotherapy,
  onRemoveFromCart,
  darkMode
}) => {
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);

  const displayQuantity = convertDailyToDisplayQuantity(item.quantity, selectedPeriod);

  const handleQuantityChange = (newDisplayQuantity: number) => {
    const dailyEquivalent = newDisplayQuantity / (selectedPeriod === 'day' ? 1 : 
                                                  selectedPeriod === 'week' ? 5 :
                                                  selectedPeriod === 'month' ? 20 : 240);
    onUpdateQuantity(item.id, Math.max(0.1, dailyEquivalent));
  };

  const handleHomeVisitPercentageChange = (newPercentage: number) => {
    onUpdateHomeVisitPercentage(item.id, newPercentage);
  };

  if (!evolution) return null;

  const janv2026Value = evolution.janv2026?.value || evolution.current.value;
  const may2026Value = evolution.may2026?.value || janv2026Value;
  const sept2026Value = evolution.september2026?.value || may2026Value;
  const july2027Value = evolution.july2027?.value || sept2026Value;
  const mostRecent2026Value = sept2026Value;

  const getCotationValueForPeriod = (period: 'current' | 'janv2026' | 'may2026' | 'sept2026' | 'july2027'): string => {
    switch (period) {
      case 'current': return item.act.cotation_valeur;
      case 'janv2026': return item.act.cotation_valeur_janv_2026 || item.act.cotation_valeur;
      case 'may2026': return item.act.cotation_valeur_may_2026 || item.act.cotation_valeur_janv_2026 || item.act.cotation_valeur;
      case 'sept2026': return item.act.cotation_valeur_sept_2026 || item.act.cotation_valeur_may_2026 || item.act.cotation_valeur_janv_2026 || item.act.cotation_valeur;
      case 'july2027': return item.act.cotation_valeur_july_2027 || item.act.cotation_valeur_sept_2026 || item.act.cotation_valeur_may_2026 || item.act.cotation_valeur_janv_2026 || item.act.cotation_valeur;
      default: return item.act.cotation_valeur;
    }
  };
  
  const currentActiveCotationValue = React.useMemo(() => {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    if (item.act.revalo_july_2027 && item.act.cotation_valeur_july_2027 && todayStr >= '2027-07-01') return item.act.cotation_valeur_july_2027;
    if (item.act.revalo_sept_2026 && item.act.cotation_valeur_sept_2026 && todayStr >= '2026-09-01') return item.act.cotation_valeur_sept_2026;
    if (item.act.revalo_may_2026 && item.act.cotation_valeur_may_2026 && todayStr >= '2026-05-28') return item.act.cotation_valeur_may_2026;
    if (item.act.revalo_july_2025 && item.act.cotation_valeur_july_2025 && todayStr >= '2025-07-01') return item.act.cotation_valeur_july_2025;
    return item.act.cotation_valeur;
  }, [item.act]);

  const mutedText = darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65';
  const bodyText = darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65';
  const valueText = darkMode ? 'text-appines-white' : 'text-appines-black';
  const inputClass = `w-20 px-2 py-1 text-center border rounded focus:ring-2 focus:ring-appines-emerald/25 focus:border-appines-emerald ${
    darkMode ? 'bg-appines-petrol border-appines-white/20 text-appines-white' : 'bg-appines-white border-[rgba(1,42,54,0.2)] text-appines-black'
  }`;

  const deltaColor = (value: number, reference: number) =>
    value > reference ? 'text-appines-emerald' : value < reference ? 'text-[#c04040]' : mutedText;
  
  return (
    <tr className={`${darkMode ? 'hover:bg-appines-white/5' : 'hover:bg-[rgba(1,42,54,0.04)]'} transition-colors`}>
      <td className="px-4 py-4">
        <div className="flex items-center">
          <div>
            <div className="flex items-center mb-1">
              <span className={`text-sm font-medium px-2 py-1 rounded ${
                darkMode ? 'bg-appines-emerald/15 text-appines-emerald' : 'bg-appines-glass/25 text-appines-petrol'
              }`}>
                {generateNgapCodeLabel(item.act, 'auto')}
              </span>
              <div className={`ml-2 flex items-center text-xs ${mutedText}`}>
                <Car className="h-3 w-3 mr-1" />
                {item.act.indemnite_deplacement === 'IFS' ? 'IFS : 4€' : 'IFD : 2,5€'}
              </div>
            </div>
            <p className={`text-sm ${bodyText}`}>{item.act.libelle}</p>
          </div>
        </div>
      </td>

      <td className="px-4 py-4">
        <div className="flex items-center space-x-2">
          <input
            type="number" min="1" max="100"
            value={displayQuantity}
            onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
            className={inputClass}
          />
          <PeriodConversionTooltip dailyQuantity={item.quantity} currentPeriod={selectedPeriod} darkMode={darkMode} />
        </div>
      </td>

      <td className="px-4 py-4">
        <div className="flex flex-col items-center space-y-1">
          <input
            type="number" min="0" max="100"
            value={item.homeVisitPercentage}
            onChange={(e) => handleHomeVisitPercentageChange(parseInt(e.target.value) || 0)}
            className={`w-16 px-2 py-1 text-center border rounded focus:ring-2 focus:ring-appines-emerald/25 focus:border-appines-emerald ${
              darkMode ? 'bg-appines-petrol border-appines-white/20 text-appines-white' : 'bg-appines-white border-[rgba(1,42,54,0.2)] text-appines-black'
            }`}
          />
          {item.homeVisitPercentage > 0 && (
            <div className="text-xs text-appines-emerald">
              +{(getDisplacementValue(item.act.indemnite_deplacement) * (item.homeVisitPercentage / 100) * displayQuantity).toFixed(2)}€
            </div>
          )}
        </div>
      </td>

      <td className="px-4 py-4">
        <BalneotherapySelector
          value={item.balneotherapy} onChange={(b) => onUpdateBalneotherapy(item.id, b)}
          darkMode={darkMode} displayQuantity={displayQuantity}
        />
      </td>

      <td className="px-4 py-4 text-right">
        <div className="text-right">
          <div className={`font-medium ${bodyText}`}>
            {evolution.original.value > 0 ? `${evolution.original.value.toFixed(2)}€` : '-'}
          </div>
          {evolution.original.value > 0 && (
            <div className={`text-xs ${mutedText}`}>
              {item.act.old_cotation_valeur || item.act.cotation_valeur} pts
            </div>
          )}
        </div>
      </td>

      <td className="px-4 py-4 text-right">
        <div className="text-right">
          <div className={`font-medium ${valueText}`}>{evolution.current.value.toFixed(2)}€</div>
          <div className={`text-xs ${mutedText}`}>{currentActiveCotationValue} pts</div>
        </div>
      </td>

      <td className="px-4 py-4 text-right">
        <div className="space-y-2">
          <div className="flex items-center justify-end space-x-2">
            <div className="text-right">
              <div className={`font-medium ${deltaColor(mostRecent2026Value, evolution.current.value)}`}>
                {mostRecent2026Value.toFixed(2)}€
              </div>
              <div className={`text-xs ${deltaColor(mostRecent2026Value, evolution.current.value)}`}>
                <div className="flex items-center justify-end">
                  <span>{getCotationValueForPeriod('sept2026')} pts</span>
                  {mostRecent2026Value !== evolution.current.value && (
                    <TrendingUp className={`h-3 w-3 ml-1 ${deltaColor(mostRecent2026Value, evolution.current.value)}`} />
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {(() => {
            const has2026Values = [evolution.janv2026, evolution.may2026, evolution.september2026].filter(Boolean).length;
            return has2026Values > 1 ? (
              <button
                onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
                className={`mt-1 p-1 rounded transition-colors ${
                  darkMode ? 'text-appines-white/45 hover:text-appines-white hover:bg-appines-white/10' : 'text-appines-petrol/45 hover:text-appines-petrol hover:bg-[rgba(1,42,54,0.06)]'
                }`}
                title={isDetailsExpanded ? 'Masquer les détails' : 'Afficher les détails'}
              >
                {isDetailsExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>
            ) : null;
          })()}
        </div>
      </td>

      <td className="px-4 py-4 text-right">
        <div className="text-right">
          <div className={`font-medium ${deltaColor(july2027Value, mostRecent2026Value)}`}>
            {july2027Value.toFixed(2)}€
          </div>
          <div className={`text-xs ${deltaColor(july2027Value, mostRecent2026Value)}`}>
            <div className="flex items-center justify-end">
              <span>{getCotationValueForPeriod('july2027')} pts</span>
              {july2027Value !== mostRecent2026Value && (
                <TrendingUp className={`h-3 w-3 ml-1 ${deltaColor(july2027Value, mostRecent2026Value)}`} />
              )}
            </div>
          </div>
          
          {isDetailsExpanded && (
            <div className={`mt-2 p-2 rounded border ${
              darkMode ? 'bg-appines-white/5 border-appines-white/12' : 'bg-[rgba(1,42,54,0.04)] border-[rgba(1,42,54,0.12)]'
            }`}>
              <div className="space-y-1 text-xs">
                {evolution.janv2026 && (
                  <div className="flex justify-between items-center">
                    <span className={mutedText}>Janv. 2026:</span>
                    <div className="text-right">
                      <div className={`font-medium ${deltaColor(evolution.janv2026.value, evolution.current.value)}`}>
                        {evolution.janv2026.value.toFixed(2)}€
                      </div>
                      <div className={`flex items-center justify-end ${mutedText}`}>
                        <span>{getCotationValueForPeriod('janv2026')} pts</span>
                      </div>
                    </div>
                  </div>
                )}
                {evolution.may2026 && (
                  <div className="flex justify-between items-center">
                    <span className={mutedText}>Mai 2026:</span>
                    <div className="text-right">
                      <div className={`font-medium ${deltaColor(evolution.may2026.value, evolution.current.value)}`}>
                        {evolution.may2026.value.toFixed(2)}€
                      </div>
                      <div className={`flex items-center justify-end ${mutedText}`}>
                        <span>{getCotationValueForPeriod('may2026')} pts</span>
                      </div>
                    </div>
                  </div>
                )}
                {evolution.september2026 && (
                  <div className="flex justify-between items-center">
                    <span className={mutedText}>Sept. 2026:</span>
                    <div className="text-right">
                      <div className={`font-medium ${deltaColor(evolution.september2026.value, evolution.current.value)}`}>
                        {evolution.september2026.value.toFixed(2)}€
                      </div>
                      <div className={`flex items-center justify-end ${mutedText}`}>
                        <span>{getCotationValueForPeriod('sept2026')} pts</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </td>

      <td className="px-4 py-4">
        <button
          onClick={() => onRemoveFromCart(item.id)}
          className={`p-2 rounded-full transition-colors ${
            darkMode ? 'text-appines-white/45 hover:text-appines-petal hover:bg-appines-petal/10' : 'text-appines-petrol/45 hover:text-[#c04040] hover:bg-appines-petal/10'
          }`}
          title="Supprimer du panier"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
};

export default SimulatorCartRow;
